// Content-free, bounded Coach interaction timing. Exported snapshots contain
// only fixed enums, a local flow sequence number, finite performance.now()
// offsets and validated request UUIDs. Business IDs used to bind a live flow to
// its output stay in process memory and are never exported. Output is proven
// only by mounted renderers reporting their exact subject and fixed output
// sink; `selector` merely snapshots pre-existing output so stale feedback is
// never counted. The first viewport-visible output freezes the flow's feedback
// latency; a bounded tail then keeps later exact-subject canonical reads and
// sink outputs, with an explicit tail status when it closes.
const FLOWS = {
  set_review: { routes: ['workout-set-save', 'exercise-recommendation-post', 'exercise-recommendation-get'], ws: ['recommendation_generation_started', 'recommendation_ready', 'workout_exercise_review_published'], sinks: ['suggestion_panel'], selector: '.exercise-coach-guidance' },
  activity_review: { routes: ['activity-position', 'activity-complete', 'coach-home'], ws: ['coach_reaction_received', 'coach_timeline_updated', 'coach_typing_stopped'], sinks: ['reaction_toast', 'review_answer_toast', 'hub_chip'], selector: '.coach-ready-toast-answer, .coach-reaction-toast .dojo-toast-coach-highlight, .coach-reaction-border-chip' },
  // daily_card: DayRecommendationCard (solo Today and historical days);
  // today_card: the dojo member's live Today renderer (DojoHub coach section).
  readiness_review: { routes: ['status-save', 'daily-recommendation'], ws: ['status_changed', 'daily_insight_available'], sinks: ['daily_card', 'today_card'], selector: '.day-recommendation-text, .dojo-hub-coach-text.daily-insight-ready' },
};
// What each mounted sink shows: a reaction emoji is not advice-text latency.
const SINK_CONTENT = { suggestion_panel: 'advice_text', reaction_toast: 'reaction', review_answer_toast: 'advice_text', hub_chip: 'reaction', daily_card: 'advice_text', today_card: 'advice_text' };
// Coach publications (output ready). Every other flow message (completion
// lifecycle, status echo, generation started, typing stopped, retry/failure
// states) is recorded as lifecycle only: it never claims ws_received and never
// starts the output wait.
const PUBLICATIONS = new Set(['workout_exercise_review_published', 'recommendation_ready', 'coach_reaction_received', 'daily_insight_available']);
const isPublication = (type, state) => PUBLICATIONS.has(type) || (type === 'coach_timeline_updated' && state === 'generated');
// body_parsed: the canonical caller holds the decoded body; context_match: the
// caller accepted that exact-subject body for its current context.
const MARKS = new Set(['admission', 'presubmit_start', 'profile_start', 'profile_end', 'gps_start', 'gps_end', 'presubmit_end',
  'save_start', 'save_end', 'followup_start', 'followup_end', 'receipt_bound', 'lifecycle_ws', 'ws_received', 'canonical_start', 'canonical_end',
  'body_parsed', 'context_match', 'dom_commit', 'viewport_visible', 'settled_failure']);
const TAIL_MARKS = new Set(['canonical_start', 'body_parsed', 'context_match', 'dom_commit', 'viewport_visible']);
// Identity fields each live message may carry: [primary, ...aliases]. Aliases
// join later subject-only messages (eventId) to the flow bound at submit (activityId).
const WS_SUBJECTS = {
  workout_exercise_review_published: ['workoutExerciseId'],
  daily_insight_available: ['statusContextId'],
  status_changed: ['statusContextId'],
  coach_reaction_received: ['activityId', 'eventId'],
  coach_timeline_updated: ['activityId', 'eventId'],
  coach_typing_stopped: ['activityId', 'eventId'],
};
const MAX_READS = 1000;
const MAX_OPEN = 20;
const MAX_DONE = 50;
const MAX_TAILS = 8;
const MAX_MARKS = 32;
const MAX_REQUESTS = 12;
const MAX_WS = 12;
const MAX_IDENTITIES = 8;
const OPEN_TTL_MS = 10 * 60 * 1000;
const OBSERVE_MS = 120 * 1000;
const TAIL_MS = 5 * 60 * 1000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
let clock = () => performance.now();
let sequence = 0;
const open = new Map();
const done = [];
// Settled flows whose bounded tail is still observing (subset of `done`).
const tails = new Map();
const subjects = new Map();
// Signals that matched no exact subject or more than one candidate flow.
const unattributed = { requests: 0, ws: 0, canonical: 0 };
const safe = work => { try { return work(); } catch (_) { return undefined; } };
const round = value => Math.round(Math.max(0, value) * 10) / 10;
const hasMark = (marks, name) => marks.some(([mark]) => mark === name);

function consoleEnabled() {
  return safe(() => window.localStorage.getItem('katafit.diagnostics.coachSequence') === '1') === true;
}

const exportMarks = (flow, marks) => marks.map(([name, at, sink]) => ({ name, at_ms: round(at - flow.origin), ...(sink ? { sink } : {}) }));
const exportWs = (flow, entries) => entries.map(([type, signal, at]) => ({ type, signal, at_ms: round(at - flow.origin) }));

// Every fixed sink of the flow kind is listed, so an output that never
// committed (or was censored by the tail window) is explicit, not missing.
function exportOutputs(flow) {
  return FLOWS[flow.kind].sinks.map(sink => {
    const output = flow.outputs.get(sink);
    if (!output) return { sink, content: SINK_CONTENT[sink], state: 'not_committed' };
    return { sink, content: SINK_CONTENT[sink], state: output.visible === null ? 'committed' : 'visible', phase: output.phase,
      commit_ms: round(output.commit - flow.origin), visible_ms: output.visible === null ? null : round(output.visible - flow.origin), visibility: output.visibility };
  });
}

function exportTail(flow) {
  if (flow.outcome === 'open') return { status: 'pending_first_output' };
  if (!flow.tail) return { status: 'not_started' };
  const tail = flow.tail;
  return { status: tail.status, window_ms: TAIL_MS, opened_ms: round(tail.opened - flow.origin), closed_ms: tail.closed == null ? null : round(tail.closed - flow.origin),
    canonical_reads: tail.reads, marks: exportMarks(flow, tail.marks), ws: exportWs(flow, tail.ws) };
}

function exportFlow(flow) {
  return {
    seq: flow.seq, flow: flow.kind, outcome: flow.outcome, visibility: flow.visibility,
    origin_ms: round(flow.origin),
    output_binding: flow.outputBinding,
    output_sink: flow.outputSink,
    output_content: flow.outputSink ? SINK_CONTENT[flow.outputSink] : null,
    canonical_reads: flow.reads,
    marks: exportMarks(flow, flow.marks),
    requests: flow.requests.map(request => ({ ...request, serverTiming: request.serverTiming.map(stage => ({ ...stage })) })),
    ws: exportWs(flow, flow.ws),
    outputs: exportOutputs(flow),
    tail: exportTail(flow),
  };
}

function snapshot() {
  // Each request carries its own measured boundary (response_headers or response_decoded).
  return { schema: 'katafit.coach_sequence.v5', clock: 'performance_now_ms', http_timing: 'per_request_boundary', first_feedback: 'first_viewport_visible_sink',
    tail_window_ms: TAIL_MS, unattributed: { ...unattributed }, open: [...open.values()].map(exportFlow), done: done.map(exportFlow) };
}

// Drops every observer and in-memory identity binding a flow still owns.
function release(flow) {
  for (const output of flow.outputs.values()) { safe(() => output.observer?.disconnect()); output.observer = null; }
  for (const [key, token] of subjects) if (token === flow) subjects.delete(key);
  flow.identities = 0;
}

function closeTail(flow, status) {
  if (!flow?.tail || flow.tail.status !== 'observing') return;
  flow.tail.status = status;
  flow.tail.closed = safe(() => clock()) ?? null;
  safe(() => flow.tail.timer && clearTimeout(flow.tail.timer));
  flow.tail.timer = null;
  tails.delete(flow.seq);
  release(flow);
  if (consoleEnabled()) safe(() => console.info('[katafit-coach-sequence]', exportFlow(flow)));
}

// A tail admits signals only inside its finite window, even if its timer is late.
function liveTail(flow, at) {
  if (flow?.tail?.status !== 'observing') return false;
  if (Number.isFinite(at) && at - flow.tail.opened <= TAIL_MS) return true;
  closeTail(flow, 'window_closed');
  return false;
}

function openTail(flow) {
  const at = flow.settledAt;
  if (!Number.isFinite(at)) { release(flow); return; }
  while (tails.size >= MAX_TAILS) closeTail(tails.values().next().value, 'evicted');
  flow.tail = { status: 'observing', opened: at, closed: null, marks: [], ws: [], reads: 0, timer: null };
  flow.tail.timer = setTimeout(() => closeTail(flow, 'window_closed'), TAIL_MS);
  tails.set(flow.seq, flow);
}

function clear() {
  for (const flow of open.values()) { safe(() => flow.timer && clearTimeout(flow.timer)); flow.timer = null; release(flow); }
  for (const flow of tails.values()) { safe(() => flow.tail.timer && clearTimeout(flow.tail.timer)); flow.tail.timer = null; release(flow); }
  open.clear(); tails.clear(); done.length = 0; subjects.clear();
  unattributed.requests = 0; unattributed.ws = 0; unattributed.canonical = 0;
}

// Account/auth identity change: open flows, tails, observers and subject
// bindings of the previous principal must never attach signals of the next one.
let identity;
export function resetCoachSequenceForIdentity(next) {
  safe(() => {
    const value = next == null ? null : String(next);
    if (identity !== undefined && value !== identity) clear();
    identity = value;
  });
}

function expose() {
  safe(() => { if (typeof window !== 'undefined') window.katafitCoachSequenceDiagnostics = { snapshot, clear }; });
}

// Freezes the first-feedback outcome. Output-bearing outcomes keep their exact
// identities for the bounded tail; every other outcome releases them now.
function settle(flow, outcome) {
  if (!flow || flow.outcome !== 'open') return;
  flow.outcome = outcome;
  flow.settledAt = safe(() => clock());
  safe(() => flow.timer && clearTimeout(flow.timer));
  flow.timer = null;
  open.delete(flow.seq);
  if (done.length === MAX_DONE) closeTail(done.shift(), 'evicted');
  done.push(flow);
  if (outcome === 'visible' || outcome === 'committed_not_visible') openTail(flow);
  else release(flow);
  if (consoleEnabled()) safe(() => console.info('[katafit-coach-sequence]', exportFlow(flow)));
}

function evict(now) {
  for (const flow of [...open.values()]) if (now - flow.origin > OPEN_TTL_MS) settle(flow, 'expired');
  for (const flow of [...tails.values()]) liveTail(flow, now);
  while (open.size >= MAX_OPEN) settle(open.values().next().value, 'evicted');
}

// `inputTimeStamp` is an Event.timeStamp on the same monotonic clock as
// performance.now(); it captures the gesture before handler admission.
export function beginCoachFlow(kind, inputTimeStamp) {
  return safe(() => {
    if (!FLOWS[kind]) return null;
    const now = clock();
    evict(now);
    const origin = Number.isFinite(inputTimeStamp) && inputTimeStamp > 0 && inputTimeStamp <= now && now - inputTimeStamp < 60000 ? inputTimeStamp : now;
    const flow = { seq: ++sequence, kind, origin, outcome: 'open', visibility: 'unknown', outputBinding: 'none', outputSink: null, marks: [['admission', now]], requests: [], ws: [],
      reads: 0, lastRead: null, baseline: null, timer: null, outputs: new Map(), identities: 0, settledAt: null, tail: null };
    flow.baseline = safe(() => (typeof document === 'undefined' ? null : baselineOf(FLOWS[kind].selector))) || null;
    open.set(flow.seq, flow);
    expose();
    return flow;
  }) || null;
}

function pushMark(flow, name, at, sink) {
  if (!flow || flow.outcome !== 'open' || !MARKS.has(name) || flow.marks.length >= MAX_MARKS || !Number.isFinite(at)) return;
  flow.marks.push(sink ? [name, at, sink] : [name, at]);
}

function pushTailMark(flow, name, at, sink) {
  const tail = flow.tail;
  if (!TAIL_MARKS.has(name) || tail.marks.length >= MAX_MARKS || !Number.isFinite(at)) return;
  tail.marks.push(sink ? [name, at, sink] : [name, at]);
}

export function markCoachFlow(flow, name) {
  safe(() => pushMark(flow, name, clock()));
}

export function failCoachFlow(flow) {
  safe(() => { markCoachFlow(flow, 'settled_failure'); settle(flow, 'failed'); });
}

const validSubject = value => typeof value === 'string' && value.length > 0 && value.length <= 128;

// At most MAX_IDENTITIES keys per flow. A newer flow binding the same subject
// supersedes an older tail, which then stops observing.
function bindSubject(flow, subject) {
  const key = `${flow.kind}:${subject}`;
  const previous = subjects.get(key);
  if (previous === flow) return true;
  if (flow.identities >= MAX_IDENTITIES) return false;
  if (previous) {
    if (previous.outcome === 'open') previous.identities -= 1;
    else closeTail(previous, 'superseded');
  }
  subjects.set(key, flow);
  flow.identities += 1;
  return true;
}

// The subject key binds later live signals to this exact flow, in memory only.
export function bindCoachFlowSubject(flow, subject) {
  safe(() => {
    if (!flow || flow.outcome !== 'open' || !validSubject(subject)) return;
    if (bindSubject(flow, subject)) markCoachFlow(flow, 'receipt_bound');
  });
}

// Exactly one candidate or nothing: concurrent same-kind flows are never guessed.
function soleOpen(predicate) {
  const flows = [...open.values()].filter(predicate);
  return flows.length === 1 ? flows[0] : null;
}

// Called by requestPerformance for every recorded route. Attaches only when a
// single open flow expects that route and started before it; ambiguous or
// unmatched requests are counted, never attributed. Tails take no requests.
export function noteCoachRequest(record) {
  safe(() => {
    const expects = candidate => FLOWS[candidate.kind].routes.includes(record.route) && candidate.origin <= record.startMs;
    const flow = soleOpen(expects);
    if (!flow) { if ([...open.values()].some(expects)) unattributed.requests += 1; return; }
    if (flow.requests.length >= MAX_REQUESTS) return;
    flow.requests.push({ attribution: 'sole_open_flow', boundary: ['response_headers', 'response_decoded', 'after_body_handling'].includes(record.boundary) ? record.boundary : 'unspecified',
      route: record.route, outcome: record.outcome, status: record.status, requestId: UUID.test(record.requestId || '') ? record.requestId : null,
      start_ms: round(record.startMs - flow.origin), end_ms: round(record.endMs - flow.origin),
      totalMs: record.totalMs, authMs: record.authMs, transportMs: record.transportMs,
      serverTiming: Array.isArray(record.serverTiming) ? record.serverTiming.slice(0, 32) : [],
    });
  });
}

// After an exact publication signal, only a mounted renderer reporting the
// same subject proves output; otherwise the flow settles as no_output.
function startOutputObservation(flow) {
  // Each later exact publication before output re-arms the bounded wait.
  if (flow.timer && hasMark(flow.marks, 'dom_commit')) return;
  safe(() => flow.timer && clearTimeout(flow.timer));
  flow.timer = setTimeout(() => settle(flow, hasMark(flow.marks, 'dom_commit') ? 'committed_not_visible' : 'no_output'), OBSERVE_MS);
}

// In-memory only: text is compared, never retained beyond the element's life or exported.
function baselineOf(selector) {
  const seen = new WeakMap();
  for (const element of document.querySelectorAll(selector)) seen.set(element, element.textContent);
  return seen;
}
const isNewOutput = (seen, element) => !seen.has(element) || seen.get(element) !== element.textContent;

function classify(element) {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return 'hidden_document';
  const rect = element.getBoundingClientRect?.();
  if (!rect) return 'unknown';
  if (rect.width === 0 || rect.height === 0) return 'collapsed';
  return 'offscreen';
}

// One observer per committed sink. The first sink to become viewport-visible
// settles the open flow and names itself; later sinks are tail observations.
function observeViewport(flow, sink, output, element) {
  const update = visibility => { output.visibility = visibility; if (flow.outcome === 'open' && flow.outputSink === sink) flow.visibility = visibility; };
  update(classify(element));
  if (typeof IntersectionObserver === 'undefined') return;
  output.observer = new IntersectionObserver(entries => safe(() => {
    const entry = entries[entries.length - 1];
    if (!entry) return;
    if (!(entry.isIntersecting && entry.intersectionRatio > 0 && document.visibilityState !== 'hidden')) { update(classify(element)); return; }
    const at = clock();
    output.visible = at; output.visibility = 'visible';
    safe(() => output.observer?.disconnect());
    output.observer = null;
    if (flow.outcome === 'open') {
      flow.visibility = 'visible';
      flow.outputSink = sink;
      pushMark(flow, 'viewport_visible', at, sink);
      settle(flow, 'visible');
    } else if (liveTail(flow, at)) pushTailMark(flow, 'viewport_visible', at, sink);
  }));
  output.observer.observe(element);
}

// Snapshot pre-existing outputs at admission so stale feedback never counts.
export function armCoachFlowOutput(flow) {
  safe(() => {
    if (!flow || flow.outcome !== 'open' || typeof document === 'undefined') return;
    flow.baseline = baselineOf(FLOWS[flow.kind].selector);
  });
}

// The open flow, or a settled flow whose tail window is still observing.
const exactFlow = (kind, subject, at) => {
  const flow = validSubject(subject) ? subjects.get(`${kind}:${subject}`) : null;
  if (!flow) return null;
  return flow.outcome === 'open' || liveTail(flow, at) ? flow : null;
};

// Called from the actual WebSocket onmessage for every parsed message type.
// Attaches only to the flow bound to the exact subject the message carries
// (exercise id, status context id, activity/event id); a matched message also
// binds its other identities (aliases) to that flow. Subject-less or unmatched
// messages are counted only. `state` only classifies publication vs lifecycle.
export function noteCoachWebSocket(type, subject = null, aliases = [], state = null) {
  safe(() => {
    const now = clock();
    const kinds = Object.keys(FLOWS).filter(kind => FLOWS[kind].ws.includes(type));
    if (!kinds.length) return;
    const identities = [subject, ...(Array.isArray(aliases) ? aliases : [])].filter(validSubject).slice(0, 4);
    const flow = kinds.flatMap(kind => identities.map(identity => exactFlow(kind, identity, now))).find(Boolean);
    if (!flow) { unattributed.ws += 1; return; }
    // Aliases only fill unbound keys: a message never steals another flow's binding.
    for (const identity of identities) if (!subjects.has(`${flow.kind}:${identity}`)) bindSubject(flow, identity);
    const publication = isPublication(type, state);
    const entry = [type, publication ? 'publication' : 'lifecycle', now];
    if (flow.outcome !== 'open') { if (flow.tail.ws.length < MAX_WS) flow.tail.ws.push(entry); return; }
    if (flow.ws.length >= MAX_WS) return;
    flow.ws.push(entry);
    const mark = publication ? 'ws_received' : 'lifecycle_ws';
    if (!hasMark(flow.marks, mark)) pushMark(flow, mark, now);
    if (publication) startOutputObservation(flow);
  });
}

// Production onmessage entry: extracts only the fixed identity fields above.
export function noteCoachWebSocketMessage(message) {
  safe(() => {
    const fields = WS_SUBJECTS[message?.type];
    const state = typeof message?.state === 'string' ? message.state : null;
    if (!fields) return noteCoachWebSocket(message?.type, null, [], state);
    const [subject, ...aliases] = fields.map(field => (message[field] == null ? null : String(message[field])));
    return noteCoachWebSocket(message.type, subject, aliases, state);
  });
}

export function coachSequenceNow() {
  return safe(() => clock());
}

// A canonical caller holds the decoded body of a read it started at
// `startedAt`. Attributed only to a flow bound to the exact subject the
// caller/body identifies (open, or its tail after first feedback), and only if
// the read started after the flow began.
export function noteCoachCanonicalRead(kind, subject, startedAt) {
  safe(() => {
    const at = clock();
    const flow = FLOWS[kind] ? exactFlow(kind, subject, at) : null;
    if (!flow || !Number.isFinite(startedAt) || startedAt < flow.origin || startedAt > at) { unattributed.canonical += 1; return; }
    if (flow.outcome === 'open') flow.reads = Math.min(MAX_READS, flow.reads + 1);
    else flow.tail.reads = Math.min(MAX_READS, flow.tail.reads + 1);
    flow.lastRead = { startedAt, parsedAt: at };
  });
}

// The caller accepted the latest exact-subject canonical body (status context
// matched, generation no longer pending, written answer found). Each read is
// accepted at most once; first feedback marks stay frozen, later ones go to the tail.
export function noteCoachContextMatch(kind, subject) {
  safe(() => {
    const at = clock();
    const flow = FLOWS[kind] ? exactFlow(kind, subject, at) : null;
    if (!flow || !flow.lastRead) return;
    const read = flow.lastRead;
    if (flow.outcome === 'open') {
      if (hasMark(flow.marks, 'context_match')) return;
      pushMark(flow, 'canonical_start', read.startedAt);
      pushMark(flow, 'body_parsed', read.parsedAt);
      pushMark(flow, 'context_match', at);
    } else {
      if (hasMark(flow.tail.marks, 'context_match')) return;
      pushTailMark(flow, 'canonical_start', read.startedAt);
      pushTailMark(flow, 'body_parsed', read.parsedAt);
      pushTailMark(flow, 'context_match', at);
    }
    flow.lastRead = null;
  });
}

// Mounted renderers report their committed output element and fixed sink.
// The first commit per sink counts; unknown sinks never attach.
export function noteCoachOutputCommitted(kind, subject, element, sink) {
  safe(() => {
    if (!FLOWS[kind]?.sinks.includes(sink) || !element) return;
    const at = clock();
    const flow = exactFlow(kind, subject, at);
    if (!flow || flow.outputs.has(sink)) return;
    if (flow.baseline && !isNewOutput(flow.baseline, element)) return;
    const output = { commit: at, visible: null, visibility: 'unknown', observer: null, phase: flow.outcome === 'open' ? 'first_feedback' : 'tail' };
    flow.outputs.set(sink, output);
    if (flow.outcome === 'open') {
      if (!hasMark(flow.marks, 'dom_commit')) { pushMark(flow, 'dom_commit', at, sink); flow.outputSink = sink; }
      flow.outputBinding = 'exact_subject';
      if (!flow.timer) flow.timer = setTimeout(() => settle(flow, 'committed_not_visible'), OBSERVE_MS);
    } else pushTailMark(flow, 'dom_commit', at, sink);
    observeViewport(flow, sink, output, element);
  });
}

export function startCoachFlowObservation(flow) {
  safe(() => { if (flow && flow.outcome === 'open') startOutputObservation(flow); });
}

export const coachSequenceTesting = {
  setClock(next) { clock = next; },
  reset() { clear(); sequence = 0; clock = () => performance.now(); },
  limits: { MAX_OPEN, MAX_DONE, MAX_TAILS, MAX_MARKS, MAX_IDENTITIES, OPEN_TTL_MS, OBSERVE_MS, TAIL_MS },
  // Retained in-memory state, for bound/cleanup assertions only.
  retained() {
    const flows = [...open.values(), ...tails.values()];
    return { open: open.size, tails: tails.size, subjects: subjects.size,
      observers: flows.reduce((count, flow) => count + [...flow.outputs.values()].filter(output => output.observer).length, 0),
      timers: flows.filter(flow => flow.timer || flow.tail?.timer).length };
  },
};

export { snapshot as coachSequenceSnapshot, clear as clearCoachSequence };
